import { UseQueryResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { Comment, parseComment } from '../../models/Comment'

import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import * as api from '../api'
import { useApiQuery } from '../useApiQuery'
import { ReturnsComments } from '../useInvalidate'

const commentsQueryParameters = queryParameters().number('commentspage')

export default function useComments(item: { learningItemUuid: string } | { groupUuid: string }) {
  const { commentspage, set } = useQueryParameters(commentsQueryParameters)
  const setPage = useCallback((page: number) => set({ commentspage: page }, { preventScrollReset: true }), [set])

  const result: UseQueryResult<{ pageCount: number; pageSize: number; itemCount: number; comments: Comment[] }> =
    useApiQuery({
      queryKey: [
        'comments',
        ReturnsComments,
        'groupUuid' in item ? item.groupUuid : item.learningItemUuid,
        commentspage,
      ],
      placeholderData: (previousData) =>
        previousData || {
          pageCount: 1,
          pageSize: 5,
          itemCount: 0,
          comments: [],
        },
      queryFn: async () => {
        const res =
          'groupUuid' in item
            ? await api.get(`group/${encodeURIComponent(item.groupUuid)}/comment?page=${commentspage + 1}&page_size=5`)
            : await api.get(
                `course/${encodeURIComponent(item.learningItemUuid)}/comment?page=${commentspage + 1}&page_size=5`
              )
        const comments = parseComment.array(res.data)
        const pageCount = res.lastPage || 0
        const itemCount = res.total || 0
        return { pageCount, pageSize: 5, itemCount, comments }
      },
    })

  return {
    page: commentspage,
    setPage,
    comments: result.data?.comments || [],
    pageCount: result.data?.pageCount || 0,
    pageSize: result.data?.pageSize || 5,
    itemCount: result.data?.itemCount || 0,
    result,
  }
}
